import ResponsiveTable from './ResponsiveTable';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CopyLinkButton from './CopyLinkButton';
import AssigneePopup from './AssigneePopup';
import {
    faSearch, faTimes, faCheck, faBan, faInbox, faTrash, faEdit,
    faFolderOpen, faBuilding, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';
import Avatar from './Avatar';
import TaskCountdown, { hasTimeWindow } from './TaskCountdown';
import { slug, taskContextLabel, shortDate } from '../utils/taskHelpers';
import { useRecentSearches } from './RecentSearches';
import '../styles/tasks.css';
import '../styles/selftask.css';

/**
 * A one-line confirmation in the corner, gone before it is in the way.
 *
 * The shared .swal2-popup rules give every dialog a 2rem pad and a 24px title,
 * which on a toast produced a banner most of the width of the header for the
 * word "Approved" — the .swal2-toast overrides in App.css cut that back. The
 * timer is short on purpose too: an approver clearing a queue sees this once
 * per row, and it must not still be there when the next one fires.
 */
const toast = (title, icon = 'success') => Swal.fire({
    toast: true,
    position: 'top-end',
    title,
    icon,
    timer: 1400,
    showConfirmButton: false
});

/**
 * Work employees logged for themselves, waiting on someone to sign it off
 * (TaskPlan.md §15).
 *
 * Rendered as the fourth view on the Tasks page rather than its own route, for
 * the same reason Recurring is: it is still task assignment, just arriving from
 * the other direction.
 *
 * Scoping is enforced server-side — a Team Lead only ever receives their own
 * team's requests — so there is nothing role-specific in here.
 */
const SelfAssignedList = () => {
    const navigate = useNavigate();

    // Who the "approved by" line should name straight away, before the server
    // has been asked. The single-row path prefers what the server sends back;
    // this is what the bulk path has to go on.
    const me = useMemo(() => {
        try {
            const u = JSON.parse(localStorage.getItem('user'));
            return u ? { _id: u.id, name: u.name, profilePic: u.profilePic } : null;
        } catch {
            return null;
        }
    }, []);

    const [tasks, setTasks] = useState([]);
    // Which row's people are on screen, or null. Holds the whole record so
    // the popup can title itself from it.
    const [peopleOf, setPeopleOf] = useState(null);

    const [loading, setLoading] = useState(true);
    const [approvalStatus, setApprovalStatus] = useState('Pending');
    // The work status (Pending / In Progress / On Hold / Completed), which is a
    // different question from whether the request was approved.
    const [workStatus, setWorkStatus] = useState('All');
    const [mine, setMine] = useState(false);
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const recentSearch = useRecentSearches('task-requests', { value: searchTerm, onPick: setSearchTerm });
    const [deciding, setDeciding] = useState(null);
    // Ids ticked for a bulk decision, as a Set. Held here rather than as a
    // flag on each row so it survives the row objects being replaced by a
    // refetch, and so "how many are selected" is one read.
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => setCurrentPage(1), [debouncedSearch, approvalStatus, workStatus, mine, overdueOnly]);

    // `quiet` refetches without blanking the table — used after a decision,
    // where the row has already been updated on screen and this is only
    // reconciling the counts and pulling in the next page's worth.
    const fetchTasks = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const res = await api.get('/tasks/self-assigned', {
                params: {
                    page: currentPage, limit: itemsPerPage,
                    search: debouncedSearch, approvalStatus,
                    ...(workStatus !== 'All' ? { status: workStatus } : {}),
                    ...(mine ? { assignedByMe: true } : {}),
                    ...(overdueOnly ? { overdue: true } : {})
                }
            });
            setTasks(res.data.data || []);
            setTotalPages(res.data.pagination.totalPages);
            setTotalRecords(res.data.pagination.totalRecords);
        } catch (err) {
            console.error('Failed to load self-assigned tasks', err);
            Swal.fire('Error', 'Could not load self-assigned tasks.', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, debouncedSearch, approvalStatus, workStatus, mine, overdueOnly]);

    useEffect(() => { fetchTasks(); }, [fetchTasks]);

    // A tick means "this row", and after a filter change or a page turn the
    // rows are different ones.
    useEffect(() => { setSelected(new Set()); },
        [debouncedSearch, approvalStatus, workStatus, mine, overdueOnly, currentPage, itemsPerPage]);

    /**
     * Writes a decision into the rows already on screen.
     *
     * The approver should not watch a spinner to learn the answer to something
     * they just decided — the server has accepted it by the time this runs, so
     * the table catches up immediately and a quiet refetch reconciles the
     * counts afterwards. A row that no longer matches the approval filter
     * (approving while looking at "Awaiting approval") leaves the list here,
     * the same way it would on the next fetch.
     */
    const applyDecision = useCallback((ids, decision, note, decider) => {
        const hit = new Set(ids.map(String));
        setTasks(prev => {
            const patched = prev.map(t => (hit.has(String(t._id))
                ? {
                    ...t,
                    approvalStatus: decision,
                    approvalNote: note || '',
                    approvedBy: decider || t.approvedBy,
                    approvedAt: new Date().toISOString()
                }
                : t));
            return approvalStatus === 'All'
                ? patched
                : patched.filter(t => t.approvalStatus === approvalStatus);
        });
        setSelected(prev => {
            const next = new Set(prev);
            hit.forEach(id => next.delete(id));
            return next;
        });
    }, [approvalStatus]);

    const decide = async (task, decision) => {
        let note = '';

        // A rejection without a reason leaves the employee nothing to act on,
        // so the server requires one and so does this.
        if (decision === 'Rejected') {
            const { value, isConfirmed } = await Swal.fire({
                title: 'Reject this task?',
                input: 'textarea',
                inputLabel: `Tell ${task.assignees[0]?.name?.split(' ')[0] || 'them'} what to change`,
                inputPlaceholder: 'e.g. Log this under the Spectra project, not office work',
                inputValidator: (v) => (!v || !v.trim()) && 'Please give a reason',
                showCancelButton: true,
                confirmButtonColor: '#dc2626',
                confirmButtonText: 'Reject'
            });
            if (!isConfirmed) return;
            note = value;
        }

        setDeciding(task._id);
        try {
            const res = await api.put(`/tasks/${task._id}/approval`, { decision, note });
            applyDecision([task._id], decision, note, res.data?.approvedBy || me);
            toast(decision === 'Approved' ? 'Approved' : 'Rejected');
            fetchTasks({ quiet: true });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not record the decision.', 'error');
            fetchTasks({ quiet: true });
        } finally {
            setDeciding(null);
        }
    };

    /**
     * Approves everything ticked in one request.
     *
     * Only approval goes in bulk: a rejection has to carry a reason the
     * employee can act on, and one reason stretched over a page of unrelated
     * tasks is not that. Rejections stay per-row.
     *
     * The server skips anything this user may not decide rather than failing
     * the whole batch, so the count it reports back is the one worth showing.
     */
    const approveSelected = async () => {
        const ids = selectableTasks.filter(t => selected.has(String(t._id))).map(t => String(t._id));
        if (!ids.length || bulkBusy) return;

        setBulkBusy(true);
        try {
            const { data } = await api.put('/tasks/approval/bulk', { ids, decision: 'Approved' });
            const done = (data.decided || []).map(String);
            applyDecision(done.length ? done : ids, 'Approved', '', me);

            toast(data.skipped > 0
                ? `Approved ${data.decidedCount} · ${data.skipped} skipped`
                : `Approved ${data.decidedCount} task${data.decidedCount === 1 ? '' : 's'}`,
                data.skipped > 0 ? 'info' : 'success');
            fetchTasks({ quiet: true });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not approve the selected tasks.', 'error');
            fetchTasks({ quiet: true });
        } finally {
            setBulkBusy(false);
        }
    };

    const remove = async (task) => {
        const who = task.assignees[0]?.name || 'this employee';
        const result = await Swal.fire({
            title: 'Delete this task?',
            html: `<p style="margin:0 0 4px">“${task.title}” will be removed from ${who}’s board.</p>`
                + `<p style="margin:0 0 4px">Its files and discussion attachments are deleted permanently.</p>`
                + `<p style="margin:0;font-size:0.85rem;color:light-dark(#64748b, var(--theme-muted, #959ba5))">Rejecting instead lets them fix it and resubmit — deleting does not.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Yes, delete it'
        });
        if (!result.isConfirmed) return;

        setDeciding(task._id);
        try {
            await api.delete(`/tasks/${task._id}`);
            setTasks(prev => prev.filter(t => t._id !== task._id));
            toast('Task deleted');
            fetchTasks({ quiet: true });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not delete the task.', 'error');
        } finally {
            setDeciding(null);
        }
    };

    // "21 Aug 2026 -> 28 Aug 2026" is wide enough to wrap the column onto three
    // lines, and the year is the same on both ends nine times out of ten.
    const timeline = (start, due) => {
        if (!due) return '—';
        if (!start) return shortDate(due);
        const a = new Date(start), b = new Date(due);
        if (a.getFullYear() === b.getFullYear()) {
            const left = a.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            return `${left} → ${shortDate(due)}`;
        }
        return `${shortDate(start)} → ${shortDate(due)}`;
    };

    const activeFilterCount = (approvalStatus !== 'All' ? 1 : 0)
        + (workStatus !== 'All' ? 1 : 0) + (mine ? 1 : 0) + (overdueOnly ? 1 : 0) + (searchTerm ? 1 : 0);

    // Already-approved rows have nothing left to approve, so they get no
    // checkbox and "select all" does not count them.
    const selectableTasks = tasks.filter(t => t.approvalStatus !== 'Approved');
    const selectedCount = selectableTasks.filter(t => selected.has(String(t._id))).length;
    const allSelected = selectableTasks.length > 0 && selectedCount === selectableTasks.length;

    const toggleOne = (id) => setSelected(prev => {
        const next = new Set(prev);
        const key = String(id);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Select-all is scoped to the page on screen, which is also what the bulk
    // request can carry — it never quietly reaches rows the approver has not
    // seen.
    const toggleAll = () => setSelected(allSelected
        ? new Set()
        : new Set(selectableTasks.map(t => String(t._id))));

    return (
        <div className="fade-in">
            <div className="task-toolbar">
                <div className="task-toolbar-search">
                    <FontAwesomeIcon icon={faSearch} className="task-search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search task, employee, asked-by, project or type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>

                <div className="task-toolbar-filters">
                    <select
                        className={`task-filter-select ${approvalStatus !== 'All' ? 'is-active' : ''}`}
                        value={approvalStatus}
                        onChange={(e) => setApprovalStatus(e.target.value)}
                    >
                        <option value="Pending">Awaiting approval</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="All">All</option>
                    </select>

                    <select
                        className={`task-filter-select ${workStatus !== 'All' ? 'is-active' : ''}`}
                        value={workStatus}
                        onChange={(e) => setWorkStatus(e.target.value)}
                    >
                        <option value="All">Any work status</option>
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="On Hold">On Hold</option>
                        <option value="Completed">Completed</option>
                    </select>

                    <label className={`task-filter-toggle ${mine ? 'is-active' : ''}`}>
                        <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
                        <span>Asked by me</span>
                    </label>
                    <label className={`task-filter-toggle ${overdueOnly ? 'is-active' : ''}`}>
                        <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
                        <span>Overdue only</span>
                    </label>

                    {activeFilterCount > 0 && (
                        <button
                            className="task-filter-clear"
                            onClick={() => { setApprovalStatus('All'); setWorkStatus('All'); setMine(false); setOverdueOnly(false); setSearchTerm(''); }}
                        >
                            <FontAwesomeIcon icon={faTimes} /> Clear ({activeFilterCount})
                        </button>
                    )}
                </div>
            </div>

            {/* Shown whenever there is anything left to approve, not only once
                something is ticked — otherwise "select all" is only reachable
                from a checkbox the approver has to find in the table header,
                and on mobile that header is hidden entirely. */}
            {!loading && selectableTasks.length > 0 && (
                <div className="sa-bulkbar">
                    <label className="sa-bulkbar-all">
                        <input
                            type="checkbox"
                            className="sa-check"
                            checked={allSelected}
                            ref={el => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                            onChange={toggleAll}
                            disabled={bulkBusy}
                        />
                        Select all ({selectableTasks.length})
                    </label>

                    {selectedCount > 0 && (
                        <>
                            <span className="sa-bulkbar-count">{selectedCount} selected</span>
                            <button
                                type="button"
                                className="sa-bulkbar-link"
                                onClick={() => setSelected(new Set())}
                                disabled={bulkBusy}
                            >
                                Clear
                            </button>
                        </>
                    )}

                    <div className="sa-bulkbar-actions">
                        <button
                            type="button"
                            className="gts-btn primary sa-bulkbar-btn"
                            onClick={approveSelected}
                            disabled={bulkBusy || selectedCount === 0}
                            title={selectedCount === 0 ? 'Tick the tasks you want to approve' : undefined}
                        >
                            {bulkBusy
                                ? <><FontAwesomeIcon icon={faSpinner} spin /> Approving…</>
                                : <><FontAwesomeIcon icon={faCheck} /> Approve Selected{selectedCount > 0 ? ` (${selectedCount})` : ''}</>}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="control-card text-center" style={{ padding: '50px 20px', color: 'light-dark(#64748b, var(--theme-muted))' }}>
                    Loading...
                </div>
            ) : tasks.length === 0 ? (
                <div className="control-card text-center" style={{ padding: '60px 20px' }}>
                    <FontAwesomeIcon icon={faInbox} style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '15px' }} />
                    <h3 style={{ color: 'light-dark(#475569, var(--theme-body))' }}>
                        {approvalStatus === 'Pending' ? 'Nothing waiting on you' : 'Nothing here'}
                    </h3>
                    <p className="text-muted">
                        {approvalStatus === 'Pending'
                            ? 'Tasks employees log for themselves will appear here for approval.'
                            : 'Try a different filter.'}
                    </p>
                </div>
            ) : (
                <div className="employee-table-container">
                    <ResponsiveTable className="employee-table task-table sa-table">
                        <thead>
                            <tr>
                                <th className="sa-check-col">
                                    <input
                                        type="checkbox"
                                        className="sa-check"
                                        checked={allSelected}
                                        // Some but not all: the box shows a dash rather than
                                        // claiming the whole page is ticked.
                                        ref={el => { if (el) el.indeterminate = selectedCount > 0 && !allSelected; }}
                                        onChange={toggleAll}
                                        disabled={selectableTasks.length === 0 || bulkBusy}
                                        title={allSelected ? 'Clear selection' : 'Select all on this page'}
                                        aria-label="Select all tasks on this page"
                                    />
                                </th>
                                <th className="col-assignees">Employee</th>
                                <th className="col-task">Task</th>
                                <th className="col-project">Type / Project</th>
                                <th className="col-due">Timeline</th>
                                <th className="col-priority">Status</th>
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map(task => {
                                const emp = task.assignees[0];
                                const busy = deciding === task._id;
                                // Nothing left to decide on an approved row.
                                const checkable = task.approvalStatus !== 'Approved';

                                return (
                                    <tr
                                        key={task._id}
                                        className={selected.has(String(task._id)) ? 'is-selected' : ''}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => { recentSearch.save(); navigate(`/task/${task._id}`); }}
                                    >
                                        <td
                                            data-label="Select"
                                            className="sa-check-col"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {checkable && (
                                                <input
                                                    type="checkbox"
                                                    className="sa-check"
                                                    checked={selected.has(String(task._id))}
                                                    onChange={() => toggleOne(task._id)}
                                                    disabled={busy || bulkBusy}
                                                    aria-label={`Select ${task.title}`}
                                                />
                                            )}
                                        </td>

                                        <td data-label="Employee" className="col-assignees">
                                            <div className="sa-employee">
                                                <Avatar
                                                    name={emp?.name}
                                                    profilePic={emp?.profilePic}
                                                    className="sa-employee-avatar is-clickable"
                                                    onClick={(e) => { e.stopPropagation(); setPeopleOf(task); }}
                                                    title="See who is working on this"
                                                />
                                                <div>
                                                    <div className="sa-employee-name">{emp?.name || 'Unknown'}</div>
                                                    <div className="sa-employee-id">{emp?.employeeId || emp?.email}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td data-label="Task" className="col-task">
                                            <span className="task-title-cell" title={task.title}>{task.title}</span>
                                            <span className="sa-claimed">
                                                <Avatar
                                                    name={task.assignedBy?.name}
                                                    profilePic={task.assignedBy?.profilePic}
                                                    className="sa-inline-avatar"
                                                />
                                                asked by {task.assignedBy?.name || 'someone'}
                                            </span>
                                            {task.approvalStatus === 'Rejected' && task.approvalNote && (
                                                <span className="sa-note">“{task.approvalNote}”</span>
                                            )}
                                        </td>

                                        <td data-label="Type / Project" className="col-project">
                                            <span className={`task-context ${task.taskType === 'Regular Office Task' ? 'is-office' : ''}`}>
                                                <FontAwesomeIcon icon={task.taskType === 'Regular Office Task' ? faBuilding : faFolderOpen} />
                                                <span className="task-context-label">{taskContextLabel(task)}</span>
                                            </span>
                                        </td>

                                        <td data-label="Timeline" className="col-due">
                                            <span className="task-due-date">
                                                {timeline(task.startDate, task.dueDate)}
                                            </span>
                                            {hasTimeWindow(task) && <TaskCountdown task={task} compact />}
                                        </td>

                                        <td data-label="Status" className="col-priority">
                                            <div className="sa-status">
                                                <span
                                                    className={`ap-badge ${slug(task.approvalStatus)}`}
                                                    title={task.approvedBy ? `Decided by ${task.approvedBy.name}` : 'Waiting on approval'}
                                                >
                                                    {task.approvalStatus === 'Pending' ? 'Awaiting' : task.approvalStatus}
                                                </span>
                                                {/* Approval and progress are different questions: a task can be
                                                    approved and untouched, or half done and still unapproved. */}
                                                <span className={`task-status-badge ${slug(task.status)}`} title="Work status">
                                                    {task.status}
                                                </span>
                                            </div>
                                            {task.approvedBy && (
                                                <div className="sa-decider" title={`${task.approvalStatus} by ${task.approvedBy.name}`}>
                                                    <Avatar
                                                        name={task.approvedBy.name}
                                                        profilePic={task.approvedBy.profilePic}
                                                        className="sa-inline-avatar"
                                                    />
                                                    by {task.approvedBy.name}
                                                </div>
                                            )}
                                        </td>

                                        <td
                                            data-label="Actions"
                                            className="col-actions task-actions-cell"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="task-actions-inner">
                                                <CopyLinkButton
                                                    path={`/task/${task._id}`}
                                                    label="Copy link to this task"
                                                />
                                                <button
                                                    className="icon-btn" title="Edit task" aria-label="Edit task"
                                                    disabled={busy} onClick={() => navigate(`/edit-task/${task._id}`)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                {task.approvalStatus !== 'Approved' && (
                                                    <button
                                                        className="icon-btn" title="Approve" aria-label="Approve"
                                                        disabled={busy} onClick={() => decide(task, 'Approved')}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </button>
                                                )}
                                                {task.approvalStatus !== 'Rejected' && (
                                                    <button
                                                        className="icon-btn" title="Reject (they can fix and resubmit)" aria-label="Reject"
                                                        disabled={busy} onClick={() => decide(task, 'Rejected')}
                                                    >
                                                        <FontAwesomeIcon icon={faBan} />
                                                    </button>
                                                )}
                                                <button
                                                    className="icon-btn danger" title="Delete task" aria-label="Delete task"
                                                    disabled={busy} onClick={() => remove(task)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </ResponsiveTable>
                </div>
            )}

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    limit={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onLimitChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
                />
            )}

            {/* A self-assigned task has one worker, but it also has the person
                they named as asking for it — both are "who is behind this",
                and the second is the one a manager usually wants. */}
            <AssigneePopup
                open={Boolean(peopleOf)}
                onClose={() => setPeopleOf(null)}
                title="Working on this"
                subtitle={peopleOf?.title}
                people={peopleOf?.assignees || []}
            />
        </div>
    );
};

export default SelfAssignedList;
